"use client";

import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Filter, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useReturnFocus } from "@/components/ui/dialog";
import {
  BottomSheet,
  BottomSheetContent,
  BottomSheetTitle,
} from "@/components/ui/bottom-sheet";

export const toolbarQuietControlClassName = cn(
  "inline-flex h-9 shrink-0 items-center gap-1.5 rounded-md px-3 text-xs font-semibold",
  "border border-transparent bg-transparent text-muted-foreground shadow-none",
  "hover:border-white/[0.14] hover:bg-white/[0.06] hover:text-foreground",
  "active:scale-[0.98] transition-all duration-150",
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
  "disabled:cursor-not-allowed disabled:opacity-50",
);


export interface FilterPillOptions {
  active?: boolean;
  className?: string;
}

export function getFilterPillClassName(options?: FilterPillOptions): string {
  const { active, className } = options || {};
  return cn(
    "inline-flex items-center gap-2 px-3 h-9 rounded-md",
    "bg-transparent border border-transparent",
    "text-sm text-muted-foreground font-medium",
    "transition-colors duration-150 ease-out",
    "hover:bg-white/6 hover:border-white/15 hover:text-foreground",
    "active:bg-white/4",
    "focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary/40",
    "disabled:opacity-50 disabled:cursor-not-allowed",
    active && "bg-primary/8 border-primary/25 text-primary hover:bg-primary/12 hover:border-primary/35",
    className,
  );
}

export function ScopeSentenceButton({
  sentence,
  onClick,
  expanded = false,
  leading,
  className,
  "aria-label": ariaLabel,
}: {
  sentence: string;
  onClick: () => void;
  expanded?: boolean;
  leading?: React.ReactNode;
  className?: string;
  "aria-label"?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-expanded={expanded}
      aria-haspopup="dialog"
      aria-label={ariaLabel}
      className={cn(
        toolbarQuietControlClassName,
        "relative min-w-[88px] max-w-full flex-1 px-2.5 text-left text-sm font-medium",
        "before:absolute before:inset-x-0 before:-inset-y-1 before:content-['']",
        className,
      )}
    >
      {leading}
      <span className="min-w-0 flex-1 truncate">{sentence}</span>
      <svg
        className="size-3 shrink-0 opacity-40"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2.5}
        aria-hidden="true"
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
      </svg>
    </button>
  );
}

export function FiltersTrigger({
  count = 0,
  expanded = false,
  onClick,
  className,
  "aria-label": ariaLabel,
}: {
  count?: number;
  expanded?: boolean;
  onClick: () => void;
  className?: string;
  "aria-label"?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-expanded={expanded}
      data-tour="advanced-filters"
      aria-label={ariaLabel ?? (count > 0 ? `Filters (${count} active)` : "Filters")}
      className={cn(
        toolbarQuietControlClassName,
        "relative shrink-0 before:absolute before:-inset-1 before:content-['']",
        expanded && "border-white/[0.16] bg-white/[0.08] text-foreground",
        count > 0 && !expanded && "border-primary/25 bg-primary/[0.08] text-primary",
        className,
      )}
    >
      <Filter className="size-3.5" aria-hidden="true" />
      <span className="hidden sm:inline">Filters</span>
      {count > 0 ? (
        <span className="inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold text-primary-foreground tabular-nums">
          {count}
        </span>
      ) : null}
    </button>
  );
}

export type ToolFilterSheetSection = "world" | "extra";

export function ToolSheetHeader({
  title = "Filters",
  description,
  badge,
  placement = "side",
  onClose,
  children,
}: {
  title?: string;
  description?: string;
  badge?: number;
  placement?: "side" | "bottom";
  onClose: () => void;
  children?: React.ReactNode;
}) {
  return (
    <div className="flex h-14 shrink-0 items-center justify-between border-b border-white/[0.06] px-5">
      <div className="flex items-center gap-2.5">
        {placement === "bottom" ? (
          <BottomSheetTitle className="text-sm font-semibold tracking-tight text-white">
            {title}
          </BottomSheetTitle>
        ) : (
          <DialogPrimitive.Title className="text-sm font-semibold tracking-tight text-white">
            {title}
          </DialogPrimitive.Title>
        )}
        {description ? (
          <DialogPrimitive.Description className="sr-only">
            {description}
          </DialogPrimitive.Description>
        ) : null}
        {badge ? (
          <span
            className="flex h-5 min-w-5 items-center justify-center rounded bg-primary/15 px-1.5 text-[10px] font-semibold text-primary tabular-nums"
            aria-label={`${badge} active filter${badge === 1 ? "" : "s"}`}
          >
            {badge}
          </span>
        ) : null}
      </div>
      <div className="flex items-center">
        {children}
        <button
          type="button"
          onClick={onClose}
          aria-label="Close filters"
          className={cn(
            "flex min-h-[44px] min-w-[44px] items-center justify-center -mr-2 rounded-md",
            "text-muted-foreground hover:bg-white/5 hover:text-foreground",
            "transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary/50",
          )}
        >
          <X className="size-4" />
        </button>
      </div>
    </div>
  );
}

export function ToolSheetFooter({
  onResetExtras,
  resetLabel = "Reset extras",
  resetClassName,
  onApply,
  applyLabel = "Apply filters",
  applyClassName,
  loading = false,
  dirty = false,
  guidedApply = false,
}: {
  onResetExtras?: () => void;
  resetLabel?: string;
  resetClassName?: string;
  onApply?: () => void;
  applyLabel?: string;
  applyClassName?: string;
  loading?: boolean;
  dirty?: boolean;
  guidedApply?: boolean;
}) {
  return (
    <div className="flex shrink-0 items-center gap-3 border-t border-white/[0.06] bg-black/20 px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]">
      {dirty ? (
        <span
          className="flex shrink-0 items-center gap-1.5 text-[11px] font-medium text-amber-300/90"
          role="status"
        >
          <span className="size-[5px] rounded-full bg-amber-400" aria-hidden="true" />
          Unsaved
        </span>
      ) : null}
      {onResetExtras ? (
        <button
          type="button"
          onClick={onResetExtras}
          disabled={loading}
          className={cn(
            "min-h-[44px] rounded-lg border border-white/[0.08] bg-white/[0.04] px-4 text-sm font-medium text-muted-foreground transition-[background-color,color,scale] duration-150 ease-out hover:bg-white/[0.07] hover:text-foreground active:not-disabled:scale-[0.96] disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
            resetClassName,
          )}
        >
          {resetLabel}
        </button>
      ) : null}
      <button
        type="button"
        onClick={onApply}
        data-spotlight-target={guidedApply ? "Click Apply extras" : undefined}
        disabled={loading}
        className={cn(
          "min-h-[44px] flex-1 rounded-lg bg-primary px-4 text-sm font-semibold text-primary-foreground shadow-lg shadow-black/25 transition-transform duration-150 ease-out active:not-disabled:scale-[0.96] disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
          applyClassName,
        )}
      >
        {applyLabel}
      </button>
    </div>
  );
}

export function ToolFilterSheet({
  open,
  onOpenChange,
  title = "Filters",
  description,
  badge,
  world,
  bar,
  extraLead,
  extra,
  children,
  initialSection = "extra",
  placement = "side",
  footer = "apply",
  onApply,
  onResetExtras,
  applyLabel,
  applyClassName,
  resetLabel = "Reset extras",
  resetClassName,
  loading = false,
  dirty = false,
  className,
  guidedApply = false,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  description?: string;
  badge?: number;
  world?: React.ReactNode;
  bar?: React.ReactNode;
  extraLead?: React.ReactNode;
  extra?: React.ReactNode;
  children?: React.ReactNode;
  initialSection?: ToolFilterSheetSection;
  placement?: "side" | "bottom";
  footer?: "apply" | "none";
  onApply?: () => void;
  onResetExtras?: () => void;
  applyLabel?: string;
  applyClassName?: string;
  resetLabel?: string;
  resetClassName?: string;
  loading?: boolean;
  dirty?: boolean;
  className?: string;
  guidedApply?: boolean;
}) {
  const worldRef = React.useRef<HTMLElement | null>(null);
  const extraRef = React.useRef<HTMLElement | null>(null);
  const returnFocus = useReturnFocus({});

  React.useEffect(() => {
    if (!open) return;
    const target = initialSection === "world" ? worldRef.current : extraRef.current;
    target?.scrollIntoView({ block: "start" });
  }, [initialSection, open]);

  const body = (
    <>
      <ToolSheetHeader
        title={title}
        description={description}
        badge={badge}
        placement={placement}
        onClose={() => onOpenChange(false)}
      />

      <div className="scrollbar-custom scrollbar-color-primary flex-1 overflow-y-auto">
        {world ? (
          <section ref={worldRef} id="filter-sheet-world">
            {world}
          </section>
        ) : null}
        {bar ? (
          <section
            id="filter-sheet-bar"
            className="flex flex-wrap items-center gap-2 border-b border-white/[0.06] p-4"
          >
            {bar}
          </section>
        ) : null}
        <section ref={extraRef} id="filter-sheet-extra">
          {extraLead ? (
            <div className="flex flex-wrap items-center gap-2 border-b border-white/[0.06] px-4 py-3">
              {extraLead}
            </div>
          ) : null}
          {extra}
          {children}
        </section>
      </div>

      {footer === "apply" ? (
        <ToolSheetFooter
          guidedApply={guidedApply}
          onResetExtras={onResetExtras}
          resetLabel={resetLabel}
          resetClassName={resetClassName}
          onApply={onApply}
          applyLabel={applyLabel ?? "Apply filters"}
          applyClassName={applyClassName}
          loading={loading}
          dirty={dirty}
        />
      ) : null}
    </>
  );

  if (placement === "bottom") {
    return (
      <BottomSheet open={open} onOpenChange={onOpenChange}>
        <BottomSheetContent {...returnFocus} className={cn("max-h-[92svh] overflow-hidden bg-[hsl(var(--optimizer-bg))]", className)}>
          {body}
        </BottomSheetContent>
      </BottomSheet>
    );
  }

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            "fixed inset-0 z-40 bg-black/60",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "duration-200",
          )}
        />
        <DialogPrimitive.Content
          {...returnFocus}
          aria-describedby={undefined}
          className={cn(
            "fixed top-0 right-0 bottom-0 z-40 flex w-full flex-col",
            "border-l border-white/[0.08] bg-[hsl(var(--optimizer-bg))]",
            "shadow-[-20px_0_60px_rgba(0,0,0,0.5)] sm:w-[380px] lg:w-[400px]",
            "duration-200 ease-out",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right",
            className,
          )}
        >
          {body}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
